import {
  ChartConfigChartPaneElement,
  CustomSpineEvents,
  ObjectHash,
  OverlayConfigObject,
  Position,
} from '../../../../types/shared'
import BaseChart from '../../../charts/base_chart'
import { chartsByType } from '../../../charts/charts'
import {
  ChartElementType,
  IndicatorType,
  OverlayType,
  PANE_HEIGHT_DEFAULT,
  ScaleType,
  SpecificChartFunctionality,
} from '../../../constants/common'
import { indicatorsByType } from '../../../indicators/indicators'
import Chart from '../../../models/chart'
import ChartLayoutModel from '../../../models/chart_layout'
import Element from '../../../models/element'
import Quote from '../../../models/quote'
import { QuoteFetchType } from '../../../models/quote/constants'
import Overlay from '../../../overlays/overlay'
import { overlaysByType } from '../../../overlays/overlays'
import { isForexFuturesCryptoPage } from '../../../utils/chart'
import { IIndicatorsValue, getOverlayAttributesConfig } from '../helpers'

export const handleTypeChange = ({
  type,
  chartLayoutModel,
}: {
  type: ChartElementType
  chartLayoutModel: ChartLayoutModel
}) => {
  const elements = chartLayoutModel.getAllElements()
  elements.forEach((element) => {
    if (element.isChart() && element.instance.attrs.type !== type) {
      const pane = element.pane()
      const instance = chartsByType[type].fromObject(
        {
          ...element.instance.attrs,
          type,
        },
        pane
      )
      element.replace(instance)
    }
  })
}

export const handleScaleChange = ({
  scale,
  chartLayoutModel,
}: {
  scale: ScaleType
  chartLayoutModel: ChartLayoutModel
}) => {
  chartLayoutModel.getAllCharts().forEach((chartModel) => {
    chartModel.updateAttribute('scale', scale)
  })
}

export const handleQuoteAttributeChange = <T extends keyof Quote>({
  attributes,
  chartLayoutModel,
}: {
  attributes: Partial<ObjectHash<Quote[T], T>>
  chartLayoutModel: ChartLayoutModel
}) => {
  const attributesToChange = Object.keys(attributes) as T[]
  chartLayoutModel.getAllCharts().forEach((chart) => {
    const quote = chart.quote()
    const isChanged = attributesToChange.some((attribute) => attributes[attribute] !== quote[attribute])
    if (isChanged) {
      quote.updateAttributes(attributes)
      quote.fetchData({ fetchType: QuoteFetchType.Refetch })
    }
  })
}

export const handleChartAttributeChange = <T extends keyof Chart>({
  attributes,
  chartLayoutModel,
}: {
  attributes: Partial<ObjectHash<Chart[T], T>>
  chartLayoutModel: ChartLayoutModel
}) => {
  chartLayoutModel.getAllCharts().forEach((chart) => {
    chart.updateAttributes(attributes)
  })
}

export const handleOverlayReplace = ({
  overlayType,
  overlayIndex,
  chartLayoutModel,
}: {
  overlayType: OverlayType
  overlayIndex: number
  chartLayoutModel: ChartLayoutModel
}) => {
  const elements = chartLayoutModel.getAllElements()
  elements.forEach((element) => {
    if (element.isChart()) {
      const instance = overlaysByType[overlayType].fromObject<Overlay>(
        {
          type: overlayType,
        },
        element.pane()
      )

      const { inputs } = instance.getModalConfig()
      const obj: ObjectHash = {}
      inputs.forEach((element) => {
        obj[element.name] = element.value
      })
      instance.set(obj)
      const elementInstance = element.instance as BaseChart
      elementInstance.overlays[overlayIndex] = instance
      elementInstance.setOverlays(elementInstance.overlays)
      elementInstance.attrs.overlays[overlayIndex] = getOverlayAttributesConfig(instance)

      if (overlayType === OverlayType.Patterns) {
        handleQuoteAttributeChange({ attributes: { hasPatterns: true }, chartLayoutModel })
      }
      elementInstance.setupCache()
    }
  })
}

export const handleRemoveOverlayElement = ({
  overlayIndex,
  chartLayoutModel,
}: {
  overlayIndex: number
  chartLayoutModel: ChartLayoutModel
}) => {
  const elements = chartLayoutModel.getAllElements()
  elements.forEach((element) => {
    if (element.isChart()) {
      const elementInstance = element.instance as BaseChart
      if (elementInstance.overlays[overlayIndex].type === OverlayType.Patterns) {
        const quote = element.pane().chart().quote()
        quote.updateAttribute('hasPatterns', false)
      }
      elementInstance.removeOverlay(elementInstance.overlays[overlayIndex])
      elementInstance.setupCache()
    }
  })
}

export const handleAddOverlayElement = ({
  chartLayoutModel,
  overlayType,
  attrs,
}: {
  chartLayoutModel: ChartLayoutModel
  overlayType?: OverlayType
  attrs?: ObjectHash
}) => {
  const elements = chartLayoutModel.getAllElements()
  const overlayAttrs = attrs ?? { type: overlaysByType[OverlayType.None].type }
  elements.forEach((element) => {
    if (element.isChart()) {
      const overlay = overlaysByType[overlayType || OverlayType.None].fromObject<Overlay>(overlayAttrs, element.pane())
      const elementInstance = element.instance as BaseChart
      elementInstance.addOverlay(overlay)
      elementInstance.attrs.overlays = [...elementInstance.attrs.overlays, overlayAttrs] as OverlayConfigObject[]
      if (overlayType === OverlayType.Patterns) {
        handleQuoteAttributeChange({ attributes: { hasPatterns: true }, chartLayoutModel })
      }
    }
  })
}

export const handleUpdateOverlayAttributes = ({
  chartLayoutModel,
  overlayIndex,
  attrs = {},
}: {
  chartLayoutModel: ChartLayoutModel
  overlayIndex: number
  attrs?: ObjectHash
}) => {
  const elements = chartLayoutModel.getAllElements()
  elements.forEach((element) => {
    if (element.isChart()) {
      const elementInstance = element.instance as BaseChart
      elementInstance.overlays[overlayIndex].set(attrs)
      elementInstance.setOverlays(elementInstance.overlays)
      elementInstance.setupCache()
    }
  })
}

export const handleIndicatorReplace = ({
  value,
  paneIndex,
  chartLayoutModel,
}: {
  value: IndicatorType
  paneIndex: number
  chartLayoutModel: ChartLayoutModel
}) => {
  const charts = chartLayoutModel.getAllCharts()
  charts.forEach((chart) => {
    const indicatorElement = chart
      .getAllPanes()
      [paneIndex].getAllElements()
      .find((el) => el.isIndicator())

    if (!indicatorElement) return
    const instance = indicatorsByType[value].fromObject(
      {
        type: value,
      },
      indicatorElement.instance.model
    )
    const { inputs } = instance.getModalConfig()
    const obj: ObjectHash = {}
    inputs.forEach((input) => {
      obj[input.name] = input.value
    })
    instance.set(obj)
    // @ts-expect-error - Element model expect instance as CanvasElement but can receive CanvaseElement || IndicatorElement
    indicatorElement.replace(instance)
    chart.trigger(CustomSpineEvents.IndicatorsChange)
  })
}

export const handleRemoveIndicatorPane = ({
  paneIndex,
  chartLayoutModel,
}: {
  paneIndex: number
  chartLayoutModel: ChartLayoutModel
}) => {
  const charts = chartLayoutModel.getAllCharts()
  charts.forEach((chart) => {
    const indicatorElement = chart
      .getAllPanes()
      [paneIndex].getAllElements()
      .find((el) => el.isIndicator())

    if (indicatorElement) {
      indicatorElement.pane().destroyCascade()

      if (chartLayoutModel.specificChartFunctionality !== SpecificChartFunctionality.chartPage) {
        chart.getAllElements().forEach((element) => {
          element.pane().resetHeight()
        })
        const chartHeight = chart.getAllPanes().reduce((previous, current) => previous + current.height, 0)
        chart.updateAttribute('height', chartHeight)
      }
      chart.trigger(CustomSpineEvents.IndicatorsChange)
    }
  })
}

export const handleAddIndicatorPane = ({
  chartLayoutModel,
  indicatorElements,
}: {
  chartLayoutModel: ChartLayoutModel
  indicatorElements?: Array<Omit<ChartConfigChartPaneElement, 'type'> & { type: IndicatorType }>
}) => {
  const charts = chartLayoutModel.getAllCharts()
  charts.forEach((chart) => {
    chart.getAllElements().forEach((element) => {
      element.pane().resetHeight()
    })
    const paneProperties = {
      height: PANE_HEIGHT_DEFAULT.indicator,
      chart,
      isFirstPane: false,
      elements: indicatorElements ? indicatorElements : [{ type: IndicatorType.None }],
    }
    const pane = chart.createPaneCascade(paneProperties)

    if (chartLayoutModel.specificChartFunctionality !== SpecificChartFunctionality.chartPage) {
      const chartHeight = chart.getAllPanes().reduce((previous, current) => previous + current.height, 0)
      chart.updateAttribute('height', chartHeight)
    }

    chart.trigger('createPane', pane, paneProperties)
  })
}

export const getAllOverlays = (chartLayoutModel: ChartLayoutModel) => {
  const chartElement = chartLayoutModel
    .charts()
    .first()
    .getAllElements()
    .find((element: Element) => element.isChart())!.instance as BaseChart
  return chartElement.overlays.filter((overlay) => overlay.type !== OverlayType.None)
}

export const getAllIndicators = (chartLayoutModel: ChartLayoutModel) => {
  const indicators: IIndicatorsValue[] = []
  const chartModel = chartLayoutModel.charts().first()
  let chartIndex: number
  chartModel.getAllPanes().forEach((pane, paneIndex) => {
    pane.getAllElements().forEach((element) => {
      if (element.isChart()) {
        chartIndex = paneIndex
      }
      if (element.isIndicator()) {
        if (element.instance.type !== IndicatorType.None) {
          indicators.push({
            // @ts-expect-error - Element model expect instance as CanvasElement but can receive CanvaseElement || IndicatorElement
            elementInstance: element.instance,
            position: chartIndex === undefined ? Position.Above : Position.Below,
            paneIndex: paneIndex,
          })
        }
      }
    })
  })
  return indicators
}

export const removeEmptyIndicatorsAndOverlays = (chartLayoutModel: ChartLayoutModel) => {
  chartLayoutModel.getAllElements().forEach((element) => {
    if (element.isChart()) {
      const elementInstance = element.instance as BaseChart
      for (let i = elementInstance.overlays.length - 1; i >= 0; i--) {
        if (elementInstance.overlays[i].type === OverlayType.None) {
          elementInstance.removeOverlay(elementInstance.overlays[i])
        }
      }
    }
    if (element.isIndicator()) {
      // @ts-expect-error - Element model expect instance as CanvasElement but can receive CanvaseElement || IndicatorElement
      const elementInstance = element.instance as Indicator
      if (elementInstance.type === IndicatorType.None) {
        const pane = element.pane()
        const chart = pane.chart()
        if (chartLayoutModel.specificChartFunctionality !== SpecificChartFunctionality.chartPage) {
          chart.updateAttribute('height', chart.height - pane.height)
        }
        pane.destroyCascade()
      }
    }
  })
}

export const handleRestoreDefault = ({
  chartLayoutModel,
  forceUpdate,
}: {
  chartLayoutModel: ChartLayoutModel
  forceUpdate: () => void
}) => {
  const DEFAULT_EXTENDED_HOURS = { premarket: true, aftermarket: true }
  const DEFAULT_OVERLAYS = [
    { type: OverlayType.Sma, period: '20', color: '#dc32b363' },
    { type: OverlayType.Sma, period: '50', color: '#ff8f33c6' },
    { type: OverlayType.Sma, period: '200', color: '#dcb3326d' },
  ]
  if (!isForexFuturesCryptoPage(chartLayoutModel.specificChartFunctionality)) {
    DEFAULT_OVERLAYS.push({ type: OverlayType.Patterns, period: '', color: '#87ceef|#dc9fe5' })
  }

  const DEFAULT_INDICATORS = [
    { height: PANE_HEIGHT_DEFAULT.indicator, elements: [{ type: IndicatorType.Rsi, period: '14' }] },
  ]
  if (chartLayoutModel.specificChartFunctionality === SpecificChartFunctionality.futuresPage) {
    DEFAULT_INDICATORS.unshift({
      height: PANE_HEIGHT_DEFAULT.indicator,
      elements: [{ type: IndicatorType.Cot, period: '' }],
    })
  }

  removeEmptyIndicatorsAndOverlays(chartLayoutModel)
  const overlays = getAllOverlays(chartLayoutModel)
  for (let i = overlays.length - 1; i >= 0; i--) {
    handleRemoveOverlayElement({ overlayIndex: i, chartLayoutModel })
  }

  handleScaleChange({ scale: ScaleType.Linear, chartLayoutModel })
  handleTypeChange({ type: ChartElementType.CandleStick, chartLayoutModel })
  handleChartAttributeChange({ attributes: DEFAULT_EXTENDED_HOURS, chartLayoutModel })
  handleQuoteAttributeChange({ attributes: DEFAULT_EXTENDED_HOURS, chartLayoutModel })

  DEFAULT_OVERLAYS.forEach((overlay) => {
    handleAddOverlayElement({
      chartLayoutModel,
      overlayType: overlay.type,
      attrs: { ...overlay },
    })
  })

  const indicators = getAllIndicators(chartLayoutModel)
  for (let i = indicators.length - 1; i >= 0; i--) {
    handleRemoveIndicatorPane({ paneIndex: indicators[i].paneIndex, chartLayoutModel })
  }
  DEFAULT_INDICATORS.forEach((indicator) => {
    handleAddIndicatorPane({
      chartLayoutModel,
      indicatorElements: indicator.elements,
    })
  })

  setTimeout(() => {
    forceUpdate()
  })
}

export const getResponseChartType = (chartType: ChartElementType) => {
  switch (chartType) {
    case ChartElementType.HeikinAshi:
      return 'heikinashi'
    case ChartElementType.HollowCandleStick:
      return 'hollowcandlestick'
    case ChartElementType.LineChart:
      return 'line'
    case ChartElementType.OhlcChart:
      return 'ohlc'
    case ChartElementType.CandleStick:
    default:
      return 'candlestick'
  }
}

export const getChartElementTypeFromResponseType = (chartResponseType: string | null) =>
  chartResponseType === null
    ? null
    : Object.values(ChartElementType).find(
        (chartType: ChartElementType) => chartResponseType === getResponseChartType(chartType)
      )
